import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdCheckBox as CheckBoxIcon } from 'react-icons/md';
import { MdCheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from 'react-icons/md';
import { AppOwnerRole, AppOwnerType, OwnerForm } from 'common-types';
import { OneOfValues } from 'common-types';
import MuitipleAppOwnersInput from '../MuitipleAppOwnersInput/MuitipleAppOwnersInput';
import {
  resetAssignAppOwnerRequest,
  setOwnersFormList,
  updateAssignAppOwnerRequest,
} from '../../application-owners/store-actions/app-owners-store-actions';
import { LoadingButton } from '@mui/lab';
import { MdMailOutline as MailOutlineIcon } from 'react-icons/md';
import { MdPersonOutline as PersonOutlineIcon } from 'react-icons/md';
import { isNull } from 'lodash';
import { MdClear as ClearIcon } from 'react-icons/md';
import { MdAdd as AddIcon } from 'react-icons/md';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const AppOwnerAssignContent: FC<
  React.PropsWithChildren<AppOwnerAssignContentProps>
> = ({
  disableAppOwnerAssignSubmit,
  saveInProgress,
  onCancel,
  onSubmit,
  owners,
  appIds,
  appName,
}) => {
  const [ownersFormDraftList, setOwnersFormDraftList] = useState<OwnerForm[]>(
    [],
  );
  const [roleList, setRoleList] = useState<OneOfValues<typeof AppOwnerRole>[]>(
    [],
  );
  const { classes, cx } = useStyles();

  const addUser = useCallback(() => {
    if (ownersFormDraftList.length === 3) {
      return;
    }
    const clone = [...ownersFormDraftList];
    clone.push({
      email: '',
      name: '',
      invalidEmailMsg: '',
      invalidNameMsg: '',
    });
    setOwnersFormDraftList(clone);
    setOwnersFormList(clone);
  }, [ownersFormDraftList]);

  useEffect(() => {
    updateAssignAppOwnerRequest('appIds', appIds);
    return () => {
      resetAssignAppOwnerRequest();
    };
  }, [appIds]);

  const onSuggestedOwnerChange = useCallback(
    (selectedOwners: AppOwnerType[]) => {
      updateAssignAppOwnerRequest('owners', selectedOwners);
    },
    [],
  );

  const onOwnerChange = (index: number) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      const draft = ownersFormDraftList.map((owner, i) => {
        if (i === index) {
          return {
            ...owner,
            [e.target.name]: e.target.value,
          };
        }
        return { ...owner };
      });
      setOwnersFormDraftList(draft);
    };
  };

  const handleSubmit = useCallback(() => {
    const validatedOwnersFormList = setOwnersFormList(ownersFormDraftList);
    if (!isNull(validatedOwnersFormList)) {
      setOwnersFormDraftList(validatedOwnersFormList);
      return;
    }
    onSubmit();
  }, [onSubmit, ownersFormDraftList]);

  return (
    <Box
      sx={{
        '& .MuiAutocomplete-popper': {
          '& .MuiAutocomplete-listbox': { maxHeight: '15vh' },
          width: '17.6rem !important',
          left: '135px !important',
        },
      }}>
      {appName && (
        <Typography color='secondary.text' variant='subtitle2'>
          Selected application <b>"{appName}"</b>
        </Typography>
      )}
      <div
        role='rowheader'
        className={cx(classes.row, classes.directionColumn)}>
        <MuitipleAppOwnersInput
          onChange={onSuggestedOwnerChange}
          label='Search user name...'
          options={owners}
        />
      </div>
      <div role='row' className={cx(classes.row, classes.directionColumn)}>
        <Stack pl={1} spacing={0.2} alignItems='center' direction='row'>
          <Button
            startIcon={
              <AddIcon style={{ width: 18, height: 18 }} fontSize='small' />
            }
            color='primary'
            disabled={ownersFormDraftList.length > 2}
            onClick={addUser}
            aria-label='Add'
            size='small'>
            <Typography
              textTransform='none'
              lineHeight={2}
              color='info'
              variant='body2'>
              Add New Owner
            </Typography>
          </Button>
        </Stack>
        <div className={classes.ownersList}>
          {ownersFormDraftList.map((owner, index) => {
            return (
              <div key={index} className={classes.ownersListItem}>
                <FormControl fullWidth size='small'>
                  <TextField
                    fullWidth
                    onChange={onOwnerChange(index)}
                    placeholder='Johnny'
                    type='text'
                    name='name'
                    label='Name'
                    error={!!owner.invalidNameMsg}
                    helperText={owner.invalidNameMsg}
                    value={owner.name}
                    size='small'
                    InputProps={{
                      startAdornment: (
                        <PersonOutlineIcon
                          style={{
                            marginInlineEnd: 5,
                          }}
                          color='primary'
                          fontSize='medium'
                        />
                      ),
                    }}
                  />
                </FormControl>
                <FormControl fullWidth size='small'>
                  <TextField
                    fullWidth
                    onChange={onOwnerChange(index)}
                    placeholder='johnny@domain.com'
                    type='email'
                    name='email'
                    label='Email'
                    size='small'
                    error={!!owner.invalidEmailMsg}
                    helperText={owner.invalidEmailMsg}
                    InputProps={{
                      startAdornment: (
                        <MailOutlineIcon
                          style={{
                            marginInlineEnd: 5,
                          }}
                          color='primary'
                          fontSize='medium'
                        />
                      ),
                    }}
                  />
                </FormControl>
                <Tooltip arrow title='Remove'>
                  <IconButton
                    onClick={() => {
                      const temp = ownersFormDraftList.filter(
                        (_o, i) => i !== index,
                      );
                      setOwnersFormDraftList(temp);
                      !temp.length && setOwnersFormList([]);
                    }}
                    sx={{
                      width: 18,
                      height: 18,
                      p: 1,
                    }}
                    color='primary'
                    size='small'>
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
      <Divider />
      <div role='row' className={cx(classes.row, classes.directionColumn)}>
        <Typography
          lineHeight={2}
          color='text.secondary'
          textTransform='uppercase'
          variant='body2'>
          Choose role
        </Typography>
        {Object.entries(AppOwnerRole).map(([key, value]) => (
          <FormControlLabel
            className={classes.checkboxItem}
            disableTypography
            key={key}
            control={
              <Checkbox
                size='small'
                onChange={(
                  _event: ChangeEvent<HTMLInputElement>,
                  checked: boolean,
                ) => {
                  const updatedRoleList = checked
                    ? [...roleList, value]
                    : roleList.filter(role => role !== value);
                  setRoleList(updatedRoleList);
                  updateAssignAppOwnerRequest('roles', [...updatedRoleList]);
                }}
                checked={roleList.includes(value)}
                value={value}
                icon={icon}
                checkedIcon={checkedIcon}
              />
            }
            label={
              <Typography
                variant='body2'
                color={roleList.includes(value) ? 'primary' : 'text.primary'}>
                {key}
              </Typography>
            }
          />
        ))}
      </div>
      <Divider />
      <div
        role='row'
        className={cx(
          classes.row,
          classes.directionRow,
          classes.justifyContent,
          classes.footer,
        )}>
        <Button color='secondary' onClick={onCancel} variant='outlined'>
          <Typography variant='body2' component='span'>
            Cancel
          </Typography>
        </Button>
        <Tooltip
          arrow
          title={
            disableAppOwnerAssignSubmit
              ? 'Please fill require fields'
              : 'Submit'
          }>
          <span>
            <LoadingButton
              color='primary'
              onClick={handleSubmit}
              loading={saveInProgress}
              disabled={disableAppOwnerAssignSubmit}
              variant='outlined'>
              <Typography variant='body2' component='span'>
                Done
              </Typography>
            </LoadingButton>
          </span>
        </Tooltip>
      </div>
    </Box>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  ownersListItem: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'row',
    marginBlockStart: theme.spacing(1),
    gap: theme.spacing(1.5),
  },
  ownersList: {
    gap: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  row: {
    paddingBlock: theme.spacing(1),
    display: 'flex',
  },
  directionColumn: {
    flexDirection: 'column',
  },
  directionRow: {
    flexDirection: 'row',
  },
  justifyContent: {
    justifyContent: 'flex-end',
  },
  footer: {
    marginBlockStart: theme.spacing(0.5),
    gap: theme.spacing(1),
  },
  checkboxItem: {
    '& > .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
    },
    margin: '0 !important',
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

export interface AppOwnerAssignContentProps {
  appIds: string[];
  appName?: string;
  owners: AppOwnerType[];
  onSubmit: () => void;
  onCancel?: () => void;
  disableAppOwnerAssignSubmit: boolean;
  saveInProgress: boolean;
}

export default AppOwnerAssignContent;
